import React from 'react';
import {useNavigation} from '@react-navigation/native';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Alert, Text, View, Platform} from 'react-native';
// Conditionally import OneSignal for native platforms only
import * as oneSignalStub from '~services/web/oneSignalStub';
let OneSignal: any = null;
if (Platform.OS !== 'web') {
  OneSignal = require('react-native-onesignal').default;
} else {
  OneSignal = oneSignalStub.OneSignal;
}
import ModernButton from '~components/ModernButton';
import ModernCard from '~components/ModernCard/ModernCard';
import {useMapsContext} from '~providers/maps/zustand';
import {useUserStore} from '~providers/userStore/zustand';
import {logScreenView, logEvent, setUserId} from '~Utils/firebaseAnalytics';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography} from '~constants/design';
import Animated, {FadeInDown, SlideInUp} from 'react-native-reanimated';
import {ANALYTICS_EVENTS} from '~constants';

const LogOut = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {goBack} = useNavigation();
  const {resetMapsState} = useMapsContext();
  const {resetUser} = useUserStore();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    logScreenView('Logout', 'LogOut');
  }, []);

  const handleLogout = async () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);
    try {
      // Use OneSignal v4 API for disabling push notifications
      try {
        OneSignal.disablePush(true);
        console.log('✅ OneSignal push notifications disabled');
      } catch (error) {
        console.error('❌ Failed to disable OneSignal push notifications:', error);
      }

      // Reset maps state
      resetMapsState();

      // Log logout event
      logEvent(ANALYTICS_EVENTS.LOGOUT);
      setUserId(null);

      // Perform logout
      resetUser();

      console.log('✅ Logout completed successfully');
    } catch (error) {
      console.error('❌ Logout failed:', error);
      setIsLoading(false);

      Alert.alert(
        'Error',
        'Failed to logout. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  const handleCancel = () => {
    if (isLoading) return; // Prevent clicks during logout
    console.log('✅ Cancel button pressed, navigating back');
    goBack();
  };

  return (
    <View style={{flex: 1, backgroundColor: colors.background}}>
      <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: spacing.lg}}>
        {/* Logout Confirmation Card */}
        <Animated.View entering={FadeInDown.delay(100).duration(600)} style={{width: '100%', maxWidth: 400}}>
          <ModernCard variant="elevated" padding="xl" style={{alignItems: 'center'}}>
            <View
              style={{
                width: 100,
                height: 100,
                borderRadius: 50,
                backgroundColor: colors.warning + '20',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: spacing.xl,
              }}>
              <Text style={{fontSize: 48}}>👋</Text>
            </View>

            <Text
              style={{
                fontSize: typography.fontSize['3xl'],
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                marginBottom: spacing.md,
                textAlign: 'center',
              }}>
              {t('settings.sign_out_title') || 'Sign Out'}
            </Text>

            <Text
              style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.normal,
                color: colors.textSecondary,
                lineHeight: typography.fontSize.lg * 1.5,
                textAlign: 'center',
                marginBottom: spacing.xl,
              }}>
              {t('settings.sign_out_body') || 'Are you sure you want to sign out of your account?'}
            </Text>

            {/* Action Buttons */}
            <View style={{width: '100%', gap: spacing.md}}>
              <Animated.View entering={SlideInUp.delay(300).duration(400)}>
                <ModernButton
                  title={t('settings.sign_out') || 'Sign Out'}
                  onPress={handleLogout}
                  loading={isLoading}
                  variant="primary"
                  size="lg"
                  fullWidth
                  hapticFeedback
                  hapticType="warning"
                  style={{
                    backgroundColor: colors.error,
                  }}
                />
              </Animated.View>

              <Animated.View entering={SlideInUp.delay(400).duration(400)}>
                <ModernButton
                  title={t('generic.cancel') || 'Cancel'}
                  onPress={handleCancel}
                  variant="outline"
                  size="lg"
                  fullWidth
                  hapticFeedback
                  disabled={isLoading}
                  style={{
                    borderColor: colors.border,
                    opacity: isLoading ? 0.5 : 1,
                  }}
                  textStyle={{
                    color: colors.textSecondary,
                  }}
                />
              </Animated.View>
            </View>
          </ModernCard>
        </Animated.View>
      </View>
    </View>
  );
};

export default LogOut;
